import { forwardRef, ReactElement, useRef } from "react";

import { withSubComponent } from "@magnetic/higher-order-components";
import {
  CaretDown,
  CaretLeft,
  CaretRight,
  Sort,
  SortDown,
  SortUp
} from "@magnetic/icons";
import {
  CellStyleModule,
  ClientSideRowModelApiModule,
  ClientSideRowModelModule,
  ColumnApiModule,
  ColumnAutoSizeModule,
  CsvExportModule,
  ExternalFilterModule,
  GridOptions,
  HighlightChangesModule,
  LicenseManager,
  ModuleRegistry,
  PaginationModule,
  RenderApiModule,
  RowApiModule,
  RowAutoHeightModule,
  RowDragModule,
  RowSelectionModule,
  setupAgTestIds,
  TooltipModule,
  ValidationModule
} from "ag-grid-enterprise";
import { AgGridReact, AgGridReactProps } from "ag-grid-react";
import { renderToString } from "react-dom/server";
import {
  BulkActionBar,
  BulkActionBarContext
} from "third-party/magnetic/table";

import { EmptyContainer } from "src/components/emptyContainer";
import { gridColumnDefinitionsDefault } from "src/components/grid/typedGridResource";
import { tortugaMagneticTheme } from "src/components/grid/typedGridTheme";
import {
  type BaseTypedTableLayoutProps,
  typedGridDefaultLayout
} from "src/components/grid/typedTableUtil";
import { VFlex } from "src/components/vFlex";
import { deepMerge } from "src/utils/object";

import "./typedGrid.scss";

/**
 * Initialize AG Grid Community version with a manifest of required grid services
 */
ModuleRegistry.registerModules([
  CellStyleModule,
  ClientSideRowModelApiModule,
  ClientSideRowModelModule,
  ColumnApiModule,
  ColumnAutoSizeModule,
  CsvExportModule,
  ExternalFilterModule,
  HighlightChangesModule,
  PaginationModule,
  RowApiModule,
  RowDragModule,
  RowAutoHeightModule, // support flexible row heights based on content
  RowSelectionModule,
  TooltipModule,
  RenderApiModule, // recalculating layout after column resize
  ValidationModule // reveal internal error messages from the grid engine
]);

// placeholder and non-functional key until AG-Grid provides official enterprise key
const AG_GRID_KEY =
  "VXNpbmdfdGhpc197QUdfR3JpZH1fRW50ZXJwcmlzZV9rZXlfe0FHLTA4NTM0NH1faW5fZXhjZXNzX29mX3RoZV9saWNlbmNlX2dyYW50ZWRfaXNfbm90X3Blcm1pdHRlZF9fX1BsZWFzZV9yZXBvcnRfbWlzdXNlX3RvX2xlZ2FsQGFnLWdyaWQuY29tX19fRm9yX2hlbHBfd2l0aF9jaGFuZ2luZ190aGlzX2tleV9wbGVhc2VfY29udGFjdF9pbmZvQGFnLWdyaWQuY29tX19fe0Npc2NvX1N5c3RlbXMsX0luYy59X2lzX2dyYW50ZWRfYV97TXVsdGlwbGVfQXBwbGljYXRpb25zfV9EZXZlbG9wZXJfTGljZW5zZV9mb3JfezV9X0Zyb250LUVuZF9KYXZhU2NyaXB0X2RldmVsb3BlcnNfX19BbGxfRnJvbnQtRW5kX0phdmFTY3JpcHRfZGV2ZWxvcGVyc19uZWVkX3RvX2JlX2xpY2Vuc2VkX2luX2FkZGl0aW9uX3RvX3RoZV9vbmVzX3dvcmtpbmdfd2l0aF97QUdfR3JpZH1fRW50ZXJwcmlzZV9fX1RoaXNfa2V5X2hhc19iZWVuX2dyYW50ZWRfYV9EZXBsb3ltZW50X0xpY2Vuc2VfQWRkLW9uX2Zvcl97Mn1fUHJvZHVjdGlvbl9FbnZpcm9ubWVudHNfX19UaGlzX2tleV93b3Jrc193aXRoX3tBR19HcmlkfV9FbnRlcnByaXNlX3ZlcnNpb25zX3JlbGVhc2VkX2JlZm9yZV97MzBfRGVjZW1iZXJfMjAyNn1fX19fW3YzXV9bMDFdX01UYzVPRFU0T0Rnd01EQXdNQT09NzgzYzNhZGE2MzZjOGRkYzMzMjQ0NGM3NzVhNGIXXXX=";

LicenseManager.setLicenseKey(atob(AG_GRID_KEY));

if (!import.meta.env.PROD) {
  setupAgTestIds({ testIdAttribute: "data-qa" });
}

const NO_MATCHING_ROWS_TEXT = "No matches found";

// Accepts some custom Typed Grid props along with the entire AG Grid configurations
interface TypedGridProps extends AgGridReactProps {
  layout?: BaseTypedTableLayoutProps;
  rowData: GridOptions["rowData"];
}

export const TypedGrid = withSubComponent(
  forwardRef<HTMLDivElement, Readonly<TypedGridProps>>(
    (props, typedGridDivRef): ReactElement => {
      const layout = props.layout ?? typedGridDefaultLayout;

      // don't pass the internal keys used only by TypedGrid to AG Grid (avoids warnings)
      const typedGridKeysToRemove: Array<string> = ["layout", "children"];
      const agGridProps: AgGridReactProps = Object.fromEntries(
        Object.entries(props).filter(
          ([key]) => !typedGridKeysToRemove.includes(key)
        )
      );

      // --- build a collection of recommended starting values for the grid. Client code will override the applicable sections
      let defaultGridOptions: GridOptions = {}; // merges client definitions into a base of options with UX defaults
      if (props.gridOptions) {
        // Iconography
        const iconWidth = 20,
          iconHeight = 20;

        defaultGridOptions = {
          theme: tortugaMagneticTheme,
          defaultColDef: gridColumnDefinitionsDefault,

          // scrolling up when a checkbox is selected can occur due to several factors, particularly when using infinite scrolling or when data changes trigger a grid refresh
          suppressScrollOnNewData: true,

          // by default, don't use the Set Filter

          suppressSetFilterByDefault: true,

          // UX: don't need extra animation when sorting column
          animateRows: false,
          // data update behavior per cell. (flash visual effect)
          cellFlashDuration: 100,
          cellFadeDuration: 300,

          // Tooltip handling
          tooltipShowDelay: 500,
          tooltipHideDelay: 5000, // vanish after revealing for few seconds
          tooltipMouseTrack: false, // lock position of tooltip on hover event so it does not move with mouse

          multiSortKey: "ctrl", // Ctrl (or Command key on Mac) to use in conjunction while adding multiple column sorts

          icons: {
            // column sort
            sortAscending: renderToString(
              <SortUp width={iconWidth} height={iconHeight} />
            ),
            sortDescending: renderToString(
              <SortDown width={iconWidth} height={iconHeight} />
            ),
            sortUnSort: renderToString(
              <Sort width={iconWidth} height={iconHeight} />
            ),

            // pagination
            first: renderToString(<div />), // UX: hide the jump to first with `CaretDoubleLeft`
            previous: renderToString(
              <CaretLeft width={iconWidth} height={iconHeight} />
            ),
            next: renderToString(
              <CaretRight width={iconWidth} height={iconHeight} />
            ),
            last: renderToString(<div />), // UX: hide the jump to last with `CaretDoubleRight`

            // menu down arrow
            selectOpen: renderToString(<CaretDown width={16} height={16} />)
          },

          // AG-grid special style, some custom cell renderers reuse existing Tortuga renderers that in turn use Magnetic text
          columnTypes: {
            truncatedMagneticText: {
              cellClass: "truncated-magnetic-text"
            },

            truncatedMagneticDataType: {
              cellClass: "truncated-magnetic-data-type"
            }
          },

          // Text when no rows are present in the grid, either due to no data or filtering
          overlayComponentParams: {
            noMatchingRows: {
              overlayText: NO_MATCHING_ROWS_TEXT
            }
          }
        };

        // when pagination is set to true, define some defaults
        if (props.gridOptions.pagination) {
          Object.assign(defaultGridOptions, {
            paginationPageSize: 100,
            paginationPageSizeSelector: true,
            domLayout: "autoHeight" // using "autoHeight" should be reserved for paginated grids, as the visible row are maximum of 100 in DOM
          });
        } else {
          Object.assign(defaultGridOptions, {
            domLayout: "normal"
          });
        }
      }

      const mergedGridOptions = deepMerge(
        defaultGridOptions,
        props.gridOptions ?? {}
      );

      const gridRef = useRef<AgGridReact>(null);

      // Bulk Action Bar
      const bulkActionBar = (
        <BulkActionBarContext.Provider
          value={{
            selectAllRows: (toggle: boolean) => {
              if (toggle) {
                gridRef?.current?.api.selectAll();
              } else {
                gridRef?.current?.api.deselectAll();
              }
            },
            selectedRows: gridRef?.current?.api.getSelectedRows() ?? [],
            totalRows: props.rowData?.length ?? 0
          }}
        >
          {props.children}
        </BulkActionBarContext.Provider>
      );
      const bulkActionBarRef = useRef<HTMLDivElement>(null);

      if (!props.rowData || !props.rowData?.length) {
        return <EmptyContainer />;
      }

      return (
        <VFlex gap={0}>
          <div ref={bulkActionBarRef}>{bulkActionBar}</div>
          <div ref={typedGridDivRef} className="typed-grid" style={layout}>
            <AgGridReact
              {...agGridProps}
              ref={gridRef}
              gridOptions={mergedGridOptions}
              noRowsOverlayComponent={EmptyContainer}
            />
          </div>
        </VFlex>
      );
    }
  ),
  {
    BulkActionBar // points to a Tortuga UI clone of `Table.BulkActionBar` with exported public abstractions
    // TODO: add future subcomponent support
    // Pagination: TablePagination,
    // Drawer: SettingsDrawer,
    // StatusIcon: TableStatusIcon,
  }
);
